import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Phone, ArrowRight, Shield, Clock, FileCheck, Calculator, ChevronDown, ChevronUp, Home, Ruler, Thermometer, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';

const HeatLoadCalculations = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqItems = [
    { question: 'Can you really do a heat load calculation without visiting the property?', answer: 'Yes — for properties with dimensioned floor plans, elevations and a section drawing, a fully compliant BS EN 12831-1:2017 heat load calculation can be completed remotely. The drawings give us every dimension we need. We use your postcode for the correct weather zone and design temperature, and a questionnaire to capture construction details, glazing specifications, and any insulation upgrades. For most standard residential properties, this produces an equally reliable result to an on-site visit.' },
    { question: "What if I don't have professional drawings?", answer: "Hand-drawn sketches with accurate dimensions are often sufficient for simpler properties. Send us what you have via our contact form and we'll advise whether it's workable or whether an on-site survey would be more appropriate. We'd rather tell you upfront than produce a result we're not confident in." },
    { question: 'Why would I pay for an independent calculation when the installer offers one free?', answer: "An installer's free heat load survey is part of their sales process. The result informs the system they quote — and there is an inherent commercial incentive to oversize. An independent calculation gives you a verified figure before any installer is involved, which you can use to test whether installer quotes are specifying an appropriately sized system. For a Boiler Upgrade Scheme application, it also ensures your documentation is not tied to a single contractor." },
    { question: 'Can I use your report with any installer?', answer: 'Yes. Our report is yours. It is issued as a PDF in a standard format and can be provided to any MCS-certified heat pump installer. You are under no obligation to use any particular contractor as a result of commissioning us.' },
    { question: 'Do you cover the whole of the UK for remote calculations?', answer: 'Yes. Our remote service covers any property in England, Scotland, and Wales where adequate drawings are available. We apply the correct weather zone and design temperature for the property\'s postcode.' },
    { question: 'How long does the remote service take?', answer: 'Typically 5 working days from receipt of complete drawings and a completed client questionnaire. Faster turnaround may be available on request — contact us to discuss.' },
    { question: 'What is the Boiler Upgrade Scheme grant?', answer: 'The BUS is a UK government scheme offering grants toward the cost of installing heat pumps and biomass boilers in homes. A compliant heat load calculation completed to MCS MIS 3005-D is a mandatory part of the installation process required to claim the grant. Please verify the current grant amount at gov.uk as figures are subject to government review.' },
    { question: 'Is your report accepted by all MCS-certified installers?', answer: 'Our report is produced to BS EN 12831-1:2017 in compliance with MCS MIS 3005-D. Any MCS-certified installer should be able to use it as the design basis for their installation. If a specific installer raises a query about our report format, contact us and we will liaise with them directly.' },
    { question: 'Can you handle multiple properties — for a landlord or developer?', answer: 'Yes. We offer portfolio pricing for three or more properties instructed together. Contact us for a bespoke quote.' }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Independent Heat Load Calculation",
    "name": "Independent Heat Load Calculations for Heat Pumps",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Energy Performance Direct",
      "url": "https://energyperformancedirect.co.uk"
    },
    "areaServed": [{ "@type": "Country", "name": "United Kingdom" }],
    "description": "Independent MCS MIS 3005-D and BS EN 12831-1:2017 compliant heat load calculations. Remote nationwide from floor plans and drawings, or on-site across Devon and South West. No installer affiliation. No product to sell.",
    "offers": [
      { "@type": "Offer", "name": "Remote Heat Load Calculation", "price": "145", "priceCurrency": "GBP", "description": "Nationwide remote heat load calculation from floor plans, elevations and section drawings. Base fee for up to 5 rooms." },
      { "@type": "Offer", "name": "On-Site Heat Load Survey", "price": "195", "priceCurrency": "GBP", "description": "On-site heat load survey across Devon and South West. Base fee for up to 5 rooms." }
    ]
  };

  const pricingData = [
    { service: 'Remote Heat Load Calculation', method: 'From drawings + questionnaire', base: '£145', additional: '£25 per room' },
    { service: 'On-Site Heat Load Survey', method: 'Physical site survey', base: '£195', additional: '£30 per room' },
    { service: 'Full MCS Design Pack', method: 'On-site: heat loss + noise assessment + emitter sizing + MCS compliance cert', base: '£295', additional: '£35 per room' },
    { service: 'EPC + Remote Heat Load (bundle)', method: 'Combined remote instruction', base: '£245', additional: '£25 per room' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Helmet>
        <title>Independent Heat Load Calculations UK | Remote &amp; On-Site | Energy Performance Direct</title>
        <meta name="description" content="Truly independent, MCS-compliant heat load calculations — not tied to any installer or product. On-site surveys across Devon & South West, or remote nationwide from your floor plans and drawings. From £145." />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/independent-heat-load-calculations" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      <Navbar />
      <Breadcrumbs items={[{ label: 'Independent Heat Load Calculations', path: '/independent-heat-load-calculations' }]} />

      {/* Hero */}
      <section className="py-12 md:py-16" style={{ background: 'linear-gradient(135deg, #064e3b 0%, #065f46 50%, #047857 100%)' }}>
        <div className="max-w-5xl mx-auto px-4" data-testid="heat-load-hero">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Independent Heat Load Calculations for Heat Pumps — Remote Nationwide or On-Site Across the South West
          </h1>
          <p className="text-lg text-emerald-100 leading-relaxed max-w-3xl mb-8">
            Before any heat pump is installed, a room-by-room heat load calculation must be completed to MCS Design Standard MIS 3005-D. But most of the time, that calculation is done by the company trying to sell you the system — which means the result may not always be in your best interests. At Energy Performance Direct, we offer something different: a fully independent, MCS-compliant heat load calculation with no installer relationship and no product to sell. You receive an impartial report you can take to any heat pump installer you choose.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" data-testid="hero-cta-drawings">
              <Button size="lg" className="bg-white text-green-800 hover:bg-gray-100 font-semibold px-6">
                Send Us Your Drawings <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/booking" data-testid="hero-cta-onsite">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold px-6">
                Book an On-Site Survey <Phone className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Independence Callout */}
      <section className="py-6">
        <div className="max-w-5xl mx-auto px-4">
          <div className="bg-slate-800 rounded-xl p-6 md:p-8 text-white" data-testid="independence-callout">
            <div className="flex items-start gap-4">
              <Shield className="w-8 h-8 text-emerald-400 flex-shrink-0 mt-1" />
              <p className="text-lg leading-relaxed italic">
                "Our calculations are completely independent of any heat pump installer or manufacturer. We have no commercial interest in which system you buy or who installs it. That means your heat load figure is yours — accurate, unbiased, and portable."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-6">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="trust-signals">
            {[
              { icon: Award, text: 'Accredited by Elmhurst Energy, TrustMark & Quidos' },
              { icon: Clock, text: 'Established since 2009' },
              { icon: Shield, text: 'No installer affiliations — completely independent' },
              { icon: Home, text: 'Nationwide remote service — Devon & South West on-site' },
              { icon: FileCheck, text: 'BS EN 12831-1:2017 compliant — MCS MIS 3005-D' }
            ].map((item, idx) => (
              <div key={idx} className="flex flex-col items-center text-center p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
                <item.icon className="w-6 h-6 text-green-600 mb-2" />
                <p className="text-xs text-gray-700 font-medium leading-snug">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Independent Matters */}
      <section className="py-10">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5" data-testid="why-independent">Why Independent Matters</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            When an installer carries out a heat load calculation as part of their sales process, there is an inherent conflict of interest. A larger heat pump commands a higher sale price. An oversized system runs less efficiently, cycles on and off unnecessarily, and costs more to run — but the problem often isn't identified until after the installation. An independent heat load calculation gives you a verified figure before any installer quoting process begins. You control the number. You use it to compare quotes on a level playing field.
          </p>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Who benefits:</h3>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              'Homeowners planning a heat pump installation who want confidence before committing',
              'Anyone applying for the Boiler Upgrade Scheme (BUS) grant who needs MCS-compliant documentation independent of the installer',
              'People who have received multiple installer quotes with wildly different system sizes and want a definitive answer',
              'Self-build and renovation projects where the design team needs a heat load figure for system specification',
              'Architects and designers specifying mechanical systems independently of contractors',
              'Landlords and property managers commissioning heat pump upgrades across a portfolio'
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-100">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Two Ways */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8" data-testid="two-ways">Two Ways to Get Your Heat Load Calculation</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Remote */}
            <Card className="border-2 border-green-200 bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Ruler className="w-5 h-5 text-green-700" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Remote Nationwide — From Your Drawings</h3>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-5">
                  If you have floor plans, elevation drawings, and a section drawing of your property, we can complete a fully compliant BS EN 12831-1:2017 heat load calculation without visiting the site. This makes our service available to homeowners, architects, designers, and developers anywhere in the UK. We work from your drawings plus a short client questionnaire covering construction details, glazing specifications, insulation upgrades, and heating requirements.
                </p>

                <h4 className="font-semibold text-gray-900 text-sm mb-2">What we need:</h4>
                <ul className="space-y-2 mb-5">
                  {[
                    'Floor plan(s) showing all room dimensions (to scale or dimensioned)',
                    'Elevation drawings showing window and door positions and sizes',
                    'Section drawing (to confirm storey heights and roof construction)',
                    'Completed client questionnaire (we send this on instruction)',
                    'Property postcode (for weather zone and design temperature)'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <h4 className="font-semibold text-gray-900 text-sm mb-2">What you receive:</h4>
                <ul className="space-y-2">
                  {[
                    'Full room-by-room heat loss report (BS EN 12831-1:2017)',
                    'Total building heat demand in kW',
                    'Per-room heat demand figures suitable for emitter sizing',
                    'MCS MIS 3005-D compliant PDF report',
                    'Turnaround: typically 5 working days from receipt of complete drawings'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <FileCheck className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* On-Site */}
            <Card className="border-2 border-blue-200 bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Thermometer className="w-5 h-5 text-blue-700" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">On-Site Survey — Devon and South West</h3>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-5">
                  For properties where drawings are unavailable, or where you want a physical inspection of construction elements, insulation, and glazing, we offer on-site surveys across Devon, Cornwall, Somerset, and the wider South West. Our assessor measures every room, inspects every element, and produces the same compliant report — with the added confidence of a first-hand building inspection.
                </p>
                <div className="bg-blue-50 rounded-lg p-4 mt-auto">
                  <p className="text-sm text-blue-800 font-medium">On-site surveys include:</p>
                  <ul className="mt-2 space-y-1">
                    {[
                      'Physical measurement of every room',
                      'Inspection of wall, floor & roof construction',
                      'Assessment of glazing type, age & condition',
                      'Review of existing heating system',
                      'Same compliant PDF report as remote service'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-blue-700">
                        <CheckCircle className="w-3 h-3 text-blue-600 flex-shrink-0 mt-1" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-10">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3" data-testid="pricing-section">Pricing</h2>
          <p className="text-gray-700 mb-6">Our pricing is straightforward and published upfront — no hidden fees, no obligation to use any particular installer.</p>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200" data-testid="pricing-table">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="text-left p-4 text-sm font-semibold">Service</th>
                  <th className="text-left p-4 text-sm font-semibold">Method</th>
                  <th className="text-left p-4 text-sm font-semibold whitespace-nowrap">Base Fee (up to 5 rooms)</th>
                  <th className="text-left p-4 text-sm font-semibold whitespace-nowrap">Additional Rooms</th>
                </tr>
              </thead>
              <tbody>
                {pricingData.map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-4 text-sm font-medium text-gray-900">{row.service}</td>
                    <td className="p-4 text-sm text-gray-600">{row.method}</td>
                    <td className="p-4 text-sm font-bold text-green-700">{row.base}</td>
                    <td className="p-4 text-sm text-gray-600">{row.additional}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-3 leading-relaxed">
            "Rooms" means every separately heated space that receives its own heat loss calculation — including bedrooms, living rooms, kitchen, hallways, bathrooms, and utility rooms. All fees exclude VAT. Call us for a bespoke quote on portfolios of 3 or more properties.
          </p>
        </div>
      </section>

      {/* What Is a Heat Load Calculation */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5" data-testid="what-is-section">What Is a Heat Load Calculation?</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            A heat load calculation determines how much heating power each room in your home needs to stay comfortable on the coldest day of the year — known as the design day. Using the European standard BS EN 12831-1:2017, we calculate the heat lost through every wall, floor, roof, window and door (fabric losses), and the heat lost through air changes (ventilation losses), arriving at a total building heat demand figure. This figure tells your installer exactly what size heat pump is needed — not a guess, not a rule of thumb.
          </p>

          <div className="bg-white rounded-lg border border-amber-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Why Is It a Mandatory Requirement?</h3>
            <p className="text-gray-700 leading-relaxed">
              MCS Design Standard MIS 3005-D requires that all heat pump installations covered by MCS certification must be based on a compliant heat load calculation. This is also a prerequisite for claiming the <a href="https://www.gov.uk/apply-boiler-upgrade-scheme" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline font-medium">Boiler Upgrade Scheme (BUS) grant</a>. Without a compliant report, your installer cannot legally justify the system size under MCS, and your grant application is at risk.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-10">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6" data-testid="faq-section">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqItems.map((faq, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg bg-white overflow-hidden" data-testid={`faq-item-${idx}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                  data-testid={`faq-toggle-${idx}`}
                >
                  <span className="font-semibold text-gray-900 text-sm pr-4">{faq.question}</span>
                  {openFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {openFaq === idx && (
                  <div className="px-4 pb-4">
                    <p className="text-gray-700 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12" style={{ background: 'linear-gradient(135deg, #064e3b 0%, #065f46 50%, #047857 100%)' }}>
        <div className="max-w-3xl mx-auto px-4 text-center" data-testid="cta-section">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Get Your Independent Heat Load Report</h2>
          <p className="text-emerald-100 mb-8">
            Tell us about your property and we'll confirm which service is right for you — remote from drawings or on-site survey.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="cta-drawings">
              <Button size="lg" className="bg-white text-green-800 hover:bg-gray-100 font-semibold px-8">
                Send Us Your Drawings <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/booking" data-testid="cta-onsite">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold px-8">
                Book an On-Site Survey <Phone className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <p className="text-emerald-200 text-sm mt-6">
            <Phone className="w-4 h-4 inline mr-1" /> Call us: 01392 980077
          </p>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-8">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-wrap gap-3 justify-center text-sm">
            <Link to="/services/domestic_epc" className="text-green-600 hover:underline">Domestic EPC</Link>
            <span className="text-gray-300">|</span>
            <Link to="/services/domestic_advice" className="text-green-600 hover:underline">Domestic Energy Advice</Link>
            <span className="text-gray-300">|</span>
            <Link to="/contact" className="text-green-600 hover:underline">Contact Us</Link>
            <span className="text-gray-300">|</span>
            <Link to="/services/sap_calculation" className="text-green-600 hover:underline">SAP Calculations</Link>
            <span className="text-gray-300">|</span>
            <a href="https://www.gov.uk/apply-boiler-upgrade-scheme" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">Boiler Upgrade Scheme (gov.uk)</a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HeatLoadCalculations;
